import { FC, ReactNode, Suspense, lazy, useState } from 'react'
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  SxProps,
  Theme,
  useTheme,
} from '@mui/material'
import { Dayjs, ManipulateType } from 'dayjs'
import { DateRange } from '@mui/x-date-pickers-pro'
import { LoadedPageRightTitle, LoadedPageLeftTitle } from '../../display'
import { LoadedCalendarReportFilter } from '../../inputs'
import {
  RangeFilter,
  LONG_DATE_FORMAT,
  MEDIUM_DATE_FORMAT,
  LONG_DATE_TIME_FORMAT,
} from '$constants'
import useAppStore from '$appStore'
import { useShallow } from 'zustand/react/shallow'
import { useRevalidator, useSearchParams } from 'react-router-dom'
import { isMobile } from '$utils'

function getDateRangeText(
  start: Dayjs,
  end: Dayjs,
  dateRange: RangeFilter
): string {
  let dateRangeText = ''

  switch (dateRange) {
    case RangeFilter.Week:
      dateRangeText = `${start.format(MEDIUM_DATE_FORMAT)} - ${end.format(
        MEDIUM_DATE_FORMAT
      )}`
      break
    case RangeFilter.Month:
      dateRangeText = `${start.format(MEDIUM_DATE_FORMAT)} - ${end.format(
        MEDIUM_DATE_FORMAT
      )}`
      break
    case RangeFilter.Custom:
      dateRangeText = `${start.format(LONG_DATE_TIME_FORMAT)} - ${end.format(
        LONG_DATE_TIME_FORMAT
      )}`
      break
    case RangeFilter.Day:
    default:
      dateRangeText = start.format(LONG_DATE_FORMAT)
      break
  }
  return dateRangeText
}

type PageHeaderProps = {
  title: string
  displayLeftTitle?: boolean
  displayDateFilter?: boolean
  customBottomLeftComponent?: string
  /** Optional element rendered to the right of the title (e.g. a help icon). */
  titleAction?: ReactNode
  children?: ReactNode
}

const PageHeader: FC<PageHeaderProps> = ({
  title,
  displayLeftTitle = true,
  displayDateFilter = displayLeftTitle,
  customBottomLeftComponent,
  titleAction,
  children,
}) => {
  // Hooks
  const theme = useTheme<Theme>()
  const revalidator = useRevalidator()
  const [, setSearchParams] = useSearchParams()

  // Remote states useAppStore
  const [
    setReportsMetadata,
    { startDateTime, endDateTime, dateRange, prevDateRange },
  ] = useAppStore(
    useShallow((state) => [state.setReportsMetadata, state.reportsMetadata])
  )

  // Local state
  const [displayCalendar, setDisplayCalendar] = useState<boolean>(false)
  const [dateRangeText, setDateRangeText] = useState<string>(
    getDateRangeText(startDateTime, endDateTime, dateRange)
  )
  const [prevRangeFilter, setPrevRangeFilter] =
    useState<RangeFilter>(prevDateRange)

  const breadcrumButtonSx: SxProps<Theme> = {
    fontSize: '.7em',
    color: theme.palette.primary.dark,
    '&:hover': {
      backgroundColor: 'transparent',
      textDecoration: `underline ${theme.palette.primary.dark}`,
    },
  }

  const onClickDateRange = async (value: number, unit: ManipulateType) => {
    const start: Dayjs = startDateTime.add(value, unit)
    const end: Dayjs = endDateTime.add(value, unit)

    switch (dateRange) {
      case RangeFilter.Week:
      case RangeFilter.Month:
        setDateRangeText(
          `${start.format(MEDIUM_DATE_FORMAT)} - ${end.format(
            MEDIUM_DATE_FORMAT
          )}`
        )
        break
      case RangeFilter.Custom:
        if (prevRangeFilter === RangeFilter.Day) {
          setDateRangeText(start.format(LONG_DATE_TIME_FORMAT))
        } else if (prevRangeFilter === RangeFilter.Week) {
          setDateRangeText(
            `${start.format(LONG_DATE_TIME_FORMAT)} - ${end
              .add(1, 'day')
              .format(LONG_DATE_TIME_FORMAT)}`
          )
        } else if (prevRangeFilter === RangeFilter.Month) {
          setDateRangeText(
            `${start.format(LONG_DATE_TIME_FORMAT)} - ${end
              .set('hour', end.hour())
              .set('minute', end.minute())
              .set('second', end.second())
              .format(LONG_DATE_TIME_FORMAT)}`
          )
        }
        break
      case RangeFilter.Day:
      default:
        setDateRangeText(start.format(LONG_DATE_FORMAT))
        break
    }

    setDisplayCalendar(false)
    setReportsMetadata(start, end, dateRange, prevRangeFilter)
    setSearchParams({
      start: start.format(),
      end: end.format(),
      dateRange: dateRange,
      prevDateRange: prevRangeFilter,
    })

    /* The revalidator will trigger a re-run of the react-router loader for the page, 
    and hence a re-render of the page content that is based on that. */
    revalidator.revalidate()
  }

  const handleOnApply = async (
    dateRange: DateRange<Dayjs>,
    rangeFilter: RangeFilter
  ) => {
    if (!dateRange || !dateRange[0] || !dateRange[1]) {
      setReportsMetadata(
        startDateTime,
        endDateTime,
        rangeFilter,
        prevRangeFilter
      )
      setDisplayCalendar(false)

      /* The revalidator will trigger a re-run of the react-router loader for the page, 
    and hence a re-render of the page content that is based on that. */
      revalidator.revalidate()
      return
    }

    setReportsMetadata(dateRange[0], dateRange[1], rangeFilter, prevRangeFilter)
    setSearchParams({
      start: dateRange[0].format(),
      end: dateRange[1].format(),
      dateRange: rangeFilter,
      prevDateRange: prevRangeFilter,
    })

    switch (rangeFilter) {
      case RangeFilter.Week:
      case RangeFilter.Month:
        setDateRangeText(
          `${dateRange[0].format(MEDIUM_DATE_FORMAT)} - ${dateRange[1].format(
            MEDIUM_DATE_FORMAT
          )}`
        )
        break
      case RangeFilter.Custom:
        setDateRangeText(
          `${dateRange[0].format(
            LONG_DATE_TIME_FORMAT
          )} - ${dateRange[1].format(LONG_DATE_TIME_FORMAT)}`
        )
        break
      case RangeFilter.Day:
      default:
        setDateRangeText(dateRange[0].format(LONG_DATE_FORMAT))
        break
    }

    setDisplayCalendar(false)

    /* The revalidator will trigger a re-run of the react-router loader for the page, 
    and hence a re-render of the page content that is based on that. */
    revalidator.revalidate()
  }

  /* This type needs to be any because React.LazyExoticComponent<React.ComponentType<any>> does not 
    allow us to reutrn <></> and we would end up having import(customBottomLeftComponent || '')) without
    avoiding the import function if the customBottomLeftComponent is not defined */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const CustomBottomLeftComponent: any = customBottomLeftComponent ? (
    lazy(
      () =>
        import(`../custom-header-components/${customBottomLeftComponent}.tsx`)
    )
  ) : (
    <></>
  )

  const stackDirection = isMobile() ? 'column' : 'row'

  return (
    <Paper
      sx={{
        ...(!displayCalendar && displayDateFilter && { paddingBottom: '.2em' }),
        backgroundColor: 'rgba(0,0,0,0)',
        paddingLeft: 0,
        paddingRight: 0,
      }}
    >
      <Stack direction={stackDirection} justifyContent={'space-between'}>
        <Stack spacing={1}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <LoadedPageRightTitle text={title} />
            {titleAction}
          </Stack>
          {customBottomLeftComponent && (
            <Suspense fallback={<CircularProgress size={15} />}>
              <CustomBottomLeftComponent />
            </Suspense>
          )}
        </Stack>
        <Box>
          {displayLeftTitle && <LoadedPageLeftTitle text={dateRangeText} />}
          {displayDateFilter && (
            <Stack
              direction={'row'}
              sx={{
                justifyContent: 'flex-end',
                ...(displayCalendar && { paddingBottom: '1em' }),
                displayPrint: 'none',
                ...(isMobile() && {
                  paddingTop: '8px',
                  paddingBottom: '16px',
                  justifyContent: 'space-around',
                  fontSize: '1.1em',
                }),
              }}
            >
              {(dateRange === RangeFilter.Day ||
                (dateRange === RangeFilter.Custom &&
                  prevRangeFilter === RangeFilter.Day)) && (
                <Box>
                  <Button
                    disableRipple
                    sx={{ ...breadcrumButtonSx }}
                    onClick={() => onClickDateRange(-1, 'week')}
                  >
                    {'<< WEEK'}
                  </Button>
                  <Button
                    disableRipple
                    sx={{ ...breadcrumButtonSx }}
                    onClick={() => onClickDateRange(-1, 'day')}
                  >
                    {'< DAY'}
                  </Button>
                  <Button
                    disableRipple
                    sx={{ ...breadcrumButtonSx }}
                    onClick={() => {
                      if (dateRange !== RangeFilter.Custom) {
                        setPrevRangeFilter(dateRange)
                      }
                      setDisplayCalendar((prev) => !prev)
                    }}
                  >
                    SELECT
                  </Button>
                  <Button
                    disableRipple
                    sx={{ ...breadcrumButtonSx }}
                    onClick={() => onClickDateRange(1, 'day')}
                  >
                    {'DAY >'}
                  </Button>
                  <Button
                    disableRipple
                    sx={{ ...breadcrumButtonSx }}
                    onClick={() => onClickDateRange(1, 'week')}
                  >
                    {'WEEK >>'}
                  </Button>
                </Box>
              )}

              {(dateRange === RangeFilter.Week ||
                (dateRange === RangeFilter.Custom &&
                  prevRangeFilter === RangeFilter.Week)) && (
                <Box>
                  <Button
                    disableRipple
                    sx={{ ...breadcrumButtonSx }}
                    onClick={() => onClickDateRange(-1, 'month')}
                  >
                    {'<< MONTH'}
                  </Button>
                  <Button
                    disableRipple
                    sx={{ ...breadcrumButtonSx }}
                    onClick={() => onClickDateRange(-1, 'week')}
                  >
                    {'< WEEK'}
                  </Button>
                  <Button
                    disableRipple
                    sx={{ ...breadcrumButtonSx }}
                    onClick={() => {
                      if (dateRange !== RangeFilter.Custom) {
                        setPrevRangeFilter(dateRange)
                      }
                      setDisplayCalendar((prev) => !prev)
                    }}
                  >
                    SELECT
                  </Button>
                  <Button
                    disableRipple
                    sx={{ ...breadcrumButtonSx }}
                    onClick={() => onClickDateRange(1, 'week')}
                  >
                    {'WEEK >'}
                  </Button>
                  <Button
                    disableRipple
                    sx={{ ...breadcrumButtonSx }}
                    onClick={() => onClickDateRange(1, 'month')}
                  >
                    {'MONTH >>'}
                  </Button>
                </Box>
              )}

              {(dateRange === RangeFilter.Month ||
                (dateRange === RangeFilter.Custom &&
                  prevRangeFilter === RangeFilter.Month)) && (
                <Box>
                  <Button
                    disableRipple
                    sx={{ ...breadcrumButtonSx }}
                    onClick={() => onClickDateRange(-1, 'year')}
                  >
                    {'<< YEAR'}
                  </Button>
                  <Button
                    disableRipple
                    sx={{ ...breadcrumButtonSx }}
                    onClick={() => onClickDateRange(-1, 'month')}
                  >
                    {'< MONTH'}
                  </Button>
                  <Button
                    disableRipple
                    sx={{ ...breadcrumButtonSx }}
                    onClick={() => {
                      if (dateRange !== RangeFilter.Custom) {
                        setPrevRangeFilter(dateRange)
                      }
                      setDisplayCalendar((prev) => !prev)
                    }}
                  >
                    SELECT
                  </Button>
                  <Button
                    disableRipple
                    sx={{ ...breadcrumButtonSx }}
                    onClick={() => onClickDateRange(1, 'month')}
                  >
                    {'MONTH >'}
                  </Button>
                  <Button
                    disableRipple
                    sx={{ ...breadcrumButtonSx }}
                    onClick={() => onClickDateRange(1, 'year')}
                  >
                    {'YEAR >>'}
                  </Button>
                </Box>
              )}
            </Stack>
          )}
        </Box>
      </Stack>

      {displayDateFilter && displayCalendar && (
        <LoadedCalendarReportFilter
          dateRange={[startDateTime, endDateTime]}
          dateRangeFilter={dateRange}
          prevDateRangeFilter={prevDateRange}
          onCancel={() => setDisplayCalendar(false)}
          onApply={handleOnApply}
        />
      )}

      {children && <Box sx={{ width: '100%', marginTop: 3 }}>{children}</Box>}
    </Paper>
  )
}

export default PageHeader
